/* eslint-disable @typescript-eslint/no-explicit-any */
import IndeterminateCheckbox from 'components/base/IndeterminateCheckbox';
import { PropsWithChildren } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  ColumnDef,
  InitialTableState
} from '@tanstack/react-table';
interface UseAdvanceTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  selection?: boolean;
  sortable?: boolean;
  pagination?: boolean;
  pageSize?: number;
  selectionColumnWidth?: number | string;
  initialState?: InitialTableState;
  meta?: any;
}

const selectionColumn = {
  id: 'select',
  accessorKey: '',
  header: ({ table }: any) => (
    <IndeterminateCheckbox
      className="form-check fs-8 mb-0"
      {...{
        checked: table.getIsAllRowsSelected(),
        indeterminate: table.getIsSomeRowsSelected(),
        onChange: table.getToggleAllRowsSelectedHandler()
      }}
    />
  ),
  cell: ({ row }: any) => (
    <IndeterminateCheckbox
      className="form-check fs-8 mb-0"
      {...{
        checked: row.getIsSelected(),
        disabled: !row.getCanSelect(),
        indeterminate: row.getIsSomeSelected(),
        onChange: row.getToggleSelectedHandler()
      }}
    />
  ),
  meta: {
    headerProps: {
      style: { width: '30px' },
      className: 'ps-4 border-start border-end border-translucent ps-0'
    },
    cellProps: {
      className:
        'ps-4 border-start border-end border-translucent white-space-nowrap ps-0'
    }
  }
};

const useAdvanceTable = <T,>({
  columns,
  data,
  selection,
  sortable,
  pagination,
  pageSize,
  initialState,
  meta
}: PropsWithChildren<UseAdvanceTableProps<T>>) => {
  const state = {
    pagination: pagination
      ? { pageSize: pagination ? pageSize : data.length }
      : undefined,
    ...initialState
  };

  const reactTableOptions: any = {
    data,
    columns: selection ? [selectionColumn, ...columns] : columns,
    enableSorting: sortable,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: state,
    meta: meta
  };

  if (meta?.manualPagination) {
    reactTableOptions.manualPagination = true;
    reactTableOptions.rowCount = meta?.totalRecords ?? undefined;
    reactTableOptions.onPaginationChange =
      meta?.onPaginationChange ?? undefined;
    // Set the pagination state to be controlled externally
    reactTableOptions.state = {
      ...reactTableOptions.state,
      pagination: meta?.pagination || { pageIndex: 0, pageSize: pageSize || 10 }
    };
  }

  if (meta?.manualFiltering) {
    reactTableOptions.manualFiltering = true;
    reactTableOptions.onGlobalFilterChange =
      meta?.onGlobalFilterChange ?? undefined;
  }

  const table = useReactTable<T>(reactTableOptions);

  return table;
};

export default useAdvanceTable;
